const DAY = 86400000;

function utc(date: string): number {
  const [y, m, d] = date.split('-').map(Number) as [number, number, number];
  return Date.UTC(y, m - 1, d);
}

/** Дата рождения + n месяцев; если такого числа нет (31 → февраль), берётся последний день месяца. */
function addMonthsClamped(date: string, n: number): number {
  const [y, m, d] = date.split('-').map(Number) as [number, number, number];
  const lastDay = new Date(Date.UTC(y, m - 1 + n + 1, 0)).getUTCDate();
  return Date.UTC(y, m - 1 + n, Math.min(d, lastDay));
}

/** Возраст ребёнка от даты рождения до даты today, обе в формате 'YYYY-MM-DD'. */
export function ageParts(birthDate: string, today: string): { years: number; months: number; days: number; totalDays: number } {
  const b = utc(birthDate);
  const t = utc(today);
  const totalDays = Math.round((t - b) / DAY);
  if (totalDays < 0) return { years: 0, months: 0, days: 0, totalDays };

  const [by, bm] = birthDate.split('-').map(Number) as [number, number];
  const [ty, tm] = today.split('-').map(Number) as [number, number];
  let months = (ty - by) * 12 + (tm - bm);
  let anchor = addMonthsClamped(birthDate, months);
  if (anchor > t) {
    months -= 1;
    anchor = addMonthsClamped(birthDate, months);
  }
  const days = Math.round((t - anchor) / DAY);
  return { years: Math.floor(months / 12), months: months % 12, days, totalDays };
}

function plural(n: number, one: string, few: string, many: string): string {
  const n10 = n % 10;
  const n100 = n % 100;
  if (n10 === 1 && n100 !== 11) return one;
  if (n10 >= 2 && n10 <= 4 && (n100 < 12 || n100 > 14)) return few;
  return many;
}

/** «3 дня», «3 недели», «3 мес 13 дн», «1 год 3 мес». */
export function formatAge(birthDate: string, today: string): string {
  const a = ageParts(birthDate, today);
  if (a.totalDays < 0) return 'ещё не родился';
  if (a.years === 0 && a.months === 0) {
    if (a.days < 14) return `${a.days} ${plural(a.days, 'день', 'дня', 'дней')}`;
    const w = Math.floor(a.days / 7);
    return `${w} ${plural(w, 'неделя', 'недели', 'недель')}`;
  }
  const allMonths = a.years * 12 + a.months;
  if (a.years === 0) return a.days > 0 ? `${allMonths} мес ${a.days} дн` : `${allMonths} мес`;
  const y = `${a.years} ${plural(a.years, 'год', 'года', 'лет')}`;
  return a.months > 0 ? `${y} ${a.months} мес` : y;
}

/**
 * Точный возраст для шапки: «6 мес 1 нед 2 дн», в первый год — с числом дней: «(184 дня)».
 * День рождения — «0 дней»: так считают в роддоме и педиатры.
 */
export function formatAgeExact(birthDate: string, today: string): { main: string; days: string | null } {
  const a = ageParts(birthDate, today);
  if (a.totalDays < 0) return { main: 'ещё не родился', days: null };
  if (a.totalDays === 0) return { main: 'первый день жизни', days: null };
  if (a.totalDays < 7) return { main: `${a.totalDays} ${plural(a.totalDays, 'день', 'дня', 'дней')}`, days: null };
  const weeks = Math.floor(a.days / 7);
  const d = a.days % 7;
  const parts: string[] = [];
  if (a.years) parts.push(`${a.years} ${plural(a.years, 'год', 'года', 'лет')}`);
  if (a.months) parts.push(`${a.months} мес`);
  if (weeks) parts.push(`${weeks} нед`);
  if (d) parts.push(`${d} дн`);
  const main = parts.join(' ') || '0 дн';
  const days = a.totalDays < 366 ? `${a.totalDays} ${plural(a.totalDays, 'день', 'дня', 'дней')}` : null;
  return { main, days };
}

/** Праздник сегодня: месяц, 100 дней, годик… null — обычный день. */
export function milestoneToday(birthDate: string, today: string, name: string): { title: string; emoji: string } | null {
  const a = ageParts(birthDate, today);
  if (a.totalDays <= 0) return a.totalDays === 0 ? { title: `С днём рождения, ${name}!`, emoji: '👶' } : null;
  const allMonths = a.years * 12 + a.months;
  if (a.days === 0 && a.months === 0 && a.years > 0) return { title: `${name}: ${a.years} ${plural(a.years, 'год', 'года', 'лет')}! С днём рождения!`, emoji: '🎂' };
  if (a.days === 0 && allMonths > 0 && allMonths <= 24) return { title: `${name} сегодня ${allMonths} ${plural(allMonths, 'месяц', 'месяца', 'месяцев')}!`, emoji: '🎉' };
  if (a.totalDays % 100 === 0) return { title: `${name} сегодня ${a.totalDays} дней!`, emoji: '💯' };
  if (a.totalDays === 7) return { title: `${name} сегодня неделя!`, emoji: '🌱' };
  if (a.totalDays === 50) return { title: `${name} сегодня 50 дней!`, emoji: '✨' };
  if (a.totalDays === 1000) return { title: `${name} сегодня 1000 дней!`, emoji: '🏆' };
  return null;
}
